import React, { useEffect, useState } from "react";
import { Dialog } from "../ui/Dialog";
import { List } from "../../types";

export type DeleteListAction =
  | { type: "delete-all" }
  | { type: "move-tasks"; targetListId: string }
  | { type: "remove-from-list" };

interface DeleteConfirmDialogProps {
  listName: string;
  taskCount: number;
  // For lists only: full breakdown across visibility states so the user
  // sees completed/archived counts that the default board view normally
  // hides. When omitted, the dialog falls back to the plain taskCount line.
  listBreakdown?: { open: number; completed: number; archived: number };
  itemType?: "list" | "tag" | "task" | "subtask";
  lists?: List[];
  onCancel: () => void;
  onConfirm: (action?: DeleteListAction) => void;
}

type DeleteOption = "delete-all" | "move-tasks" | "remove-from-list";

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  listName,
  taskCount,
  listBreakdown,
  itemType = "list",
  lists = [],
  onCancel,
  onConfirm,
}) => {
  // When the list contains only completed tasks (no open or archived ones
  // left), the user is most likely cleaning up — surface "Delete list and
  // all tasks" as the default and the first option. In every other case
  // (any open or any archived tasks) keep the safer "remove from list"
  // default so a user doesn't accidentally lose tasks they still care about.
  const allTasksCompleted = !!(
    listBreakdown &&
    listBreakdown.completed > 0 &&
    listBreakdown.open === 0 &&
    listBreakdown.archived === 0
  );
  const [deleteOption, setDeleteOption] = useState<DeleteOption>(
    allTasksCompleted ? "delete-all" : "remove-from-list",
  );
  const [targetListId, setTargetListId] = useState<string>("");

  const isTag = itemType === "tag";
  const isTask = itemType === "task";
  const isSubtask = itemType === "subtask";
  // Tasks and sub-tasks share the same plain confirm UI ("delete this one
  // thing, cannot be undone"); only the noun differs.
  const isTaskLike = isTask || isSubtask;
  const itemNoun = isTask
    ? "Task"
    : isSubtask
      ? "Sub-task"
      : isTag
        ? "Tag"
        : "List";

  // Filter out the list being deleted from the dropdown options
  const availableLists = lists.filter((l) => l.name !== listName);

  // Default the move-tasks dropdown to the first available list once the
  // user picks that option.
  useEffect(() => {
    if (
      deleteOption === "move-tasks" &&
      !targetListId &&
      availableLists.length > 0
    ) {
      setTargetListId(availableLists[0].id);
    }
  }, [deleteOption, targetListId, availableLists]);

  const handleConfirm = () => {
    if (isTaskLike || isTag) {
      onConfirm();
      return;
    }
    if (taskCount === 0) {
      onConfirm({ type: "delete-all" });
      return;
    }
    if (deleteOption === "move-tasks" && targetListId) {
      onConfirm({ type: "move-tasks", targetListId });
    } else if (deleteOption === "remove-from-list") {
      onConfirm({ type: "remove-from-list" });
    } else {
      onConfirm({ type: "delete-all" });
    }
  };

  const renderListOption = (
    value: DeleteOption,
    title: string,
    subtitle: string,
    accent: "red" | "blue",
  ) => {
    const accentText =
      accent === "red"
        ? "text-red-600 focus:ring-red-500"
        : "text-warm-blue focus:ring-warm-blue";
    return (
      <label className="flex items-start gap-3 p-3 bg-warm-bg-elevated rounded-lg border border-warm-border cursor-pointer">
        <input
          type="radio"
          name="deleteOption"
          checked={deleteOption === value}
          onChange={() => setDeleteOption(value)}
          className={`mt-0.5 ${accentText}`}
        />
        <div className="flex-1">
          <div className="text-sm font-medium text-warm-text-primary">
            {title}
          </div>
          <div className="text-xs text-warm-text-secondary mt-1">{subtitle}</div>
        </div>
      </label>
    );
  };

  // For tags, tasks, sub-tasks, and empty lists there are no radio options —
  // the button just performs a plain delete (handleConfirm skips the option
  // branching). Show it as "Delete" in red so the label and color match
  // what the click actually does, instead of inheriting the (irrelevant)
  // initial `deleteOption` state.
  const isPlainDelete = isTaskLike || isTag || taskCount === 0;

  const confirmAccent =
    isPlainDelete || deleteOption === "delete-all"
      ? "bg-red-600 hover:bg-red-700"
      : "bg-warm-blue hover:bg-warm-blue-dark";

  const confirmLabel = isPlainDelete
    ? "Delete"
    : deleteOption === "move-tasks"
      ? "Move & Delete"
      : deleteOption === "remove-from-list"
        ? "Remove & Delete"
        : "Delete";

  return (
    <Dialog
      onClose={onCancel}
      label={`Delete ${itemNoun}`}
      backdropClassName="z-40"
      panelClassName="w-full max-w-md max-h-[95vh] overflow-y-auto"
    >
      <h3 className="text-lg font-semibold text-warm-text-primary mb-3">
        Delete {itemNoun}?
      </h3>
      <p className="text-warm-text-secondary text-sm mb-4">
        {isTaskLike ? (
          <>
            Are you sure you want to delete "{listName}"? This action cannot be undone.
          </>
        ) : isTag ? (
          <>
            Are you sure you want to delete the tag "{listName}"?
            {taskCount > 0 && (
              <> This tag is used on <strong>{taskCount} {taskCount === 1 ? "task" : "tasks"}</strong> and will be removed from them.</>
            )}
            {" "}This action cannot be undone.
          </>
        ) : taskCount === 0 ? (
          <>
            Are you sure you want to delete "{listName}"?
          </>
        ) : !listBreakdown ? (
          <>
            "{listName}" contains <strong>{taskCount} {taskCount === 1 ? "task" : "tasks"}</strong>. What would you like to do with the tasks?
          </>
        ) : (
          <>"{listName}" contains:</>
        )}
      </p>

      {/* Full breakdown for list deletions. Surfaces tasks the default
          board view hides (completed, archived) so the user can pause
          if they didn't realize the list still has content. */}
      {!isTaskLike && !isTag && listBreakdown && taskCount > 0 && (
        <div className="text-sm mb-4">
          <ul className="space-y-0.5 text-warm-text-primary mb-3">
            {listBreakdown.open > 0 && (
              <li>
                <strong>{listBreakdown.open}</strong>{" "}
                open {listBreakdown.open === 1 ? "task" : "tasks"}
              </li>
            )}
            {listBreakdown.completed > 0 && (
              <li>
                <strong>{listBreakdown.completed}</strong>{" "}
                completed {listBreakdown.completed === 1 ? "task" : "tasks"}
              </li>
            )}
            {listBreakdown.archived > 0 && (
              <li>
                <strong>{listBreakdown.archived}</strong>{" "}
                archived {listBreakdown.archived === 1 ? "task" : "tasks"}
              </li>
            )}
          </ul>
          <div className="text-warm-text-secondary">
            What would you like to do with the tasks?
          </div>
        </div>
      )}

      {!isTaskLike && !isTag && taskCount > 0 && (
        <div className="space-y-3 mb-4">
          {(allTasksCompleted
            ? ["delete-all", "remove-from-list", "move-tasks"]
            : ["remove-from-list", "move-tasks", "delete-all"]
          ).map((opt) => {
            if (opt === "remove-from-list") {
              return (
                <React.Fragment key="remove-from-list">
                  {renderListOption(
                    "remove-from-list",
                    "Keep them",
                    "They'll stay, but no longer belong to any list",
                    "blue",
                  )}
                </React.Fragment>
              );
            }
            if (opt === "move-tasks") {
              if (availableLists.length === 0) return null;
              return (
                <React.Fragment key="move-tasks">
                  {renderListOption(
                    "move-tasks",
                    "Move them to another list",
                    "They'll be moved to the list you pick",
                    "blue",
                  )}
                  {deleteOption === "move-tasks" && (
                    <div className="ml-9">
                      <label className="block text-xs font-medium text-warm-text-secondary mb-2">
                        Move them to:
                      </label>
                      <select
                        value={targetListId}
                        onChange={(e) => setTargetListId(e.target.value)}
                        className="w-full bg-warm-bg-card border border-warm-border rounded-lg px-3 py-2 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
                      >
                        {availableLists.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.emoji} {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key="delete-all">
                {renderListOption(
                  "delete-all",
                  "Delete them",
                  "They'll be permanently deleted along with the list",
                  "red",
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {!isTaskLike && !isTag && taskCount === 0 && (
        <p className="text-sm text-warm-text-secondary mb-4">
          This action cannot be undone.
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          className={`flex-1 px-4 py-2 ${confirmAccent} text-white rounded-lg text-sm transition-colors`}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
